import { Button as UiButton } from 'app/components/ui/button'
import { formatCurrency } from 'lib/formatCurrency'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  CalendarCheckIcon,
  CaretDownIcon,
  ClockCounterClockwiseIcon,
  ClockIcon,
  FireIcon,
  LightningIcon,
  PhoneIcon,
  PlayIcon,
  ToothIcon,
  XIcon,
} from '@phosphor-icons/react'
import Companies from 'app/components/Icon/CompaniesIcon'
import moment from 'moment'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import AudioPlayer from 'app/components/AudioPlayer'
import Button from 'app/components/Button'
import { FollowUpForm } from 'app/components/activities/FollowUpForm'
import { CallInitiationModal } from 'app/components/click-to-call/CallInitiationModal'
import { OutboundCallProvider } from 'app/components/click-to-call/OutboundCallProvider'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { LeadStatusTrigger } from 'app/components/contacts/LeadStatusTrigger'
import { QuickCreateTaskModal } from 'app/components/tasks/QuickCreateTaskModal'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { ActionPlay, actionPlayFor, FAMILY_ACCENT } from './queueRegistry'
import { showClosedLostQuickModal } from './ClosedLostQuickForm'
import { PipelineRail } from './PipelineRail'
import { PracticeListRail } from './PracticeListRail'
import { getPreferredSnoozeDays, setPreferredSnoozeDays } from './momentumStore'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useListCopy } from 'app/hooks/useListCopy'
import { useLatestAppointment } from './useLatestAppointment'
import { useLocationsInfo } from './useLocationNames'
import { usePipelinesCatalog } from './usePipelinesCatalog'
import { ConversationPanel } from './ConversationPanel'
import { DateTile } from './DateTile'
import { FocusActionBar } from './FocusActionBar'
import { HygieneTimeline } from './HygieneTimeline'
import { LastActivityPanel } from './LastActivityPanel'
import { NavArrow } from './NavArrow'
import { QueueViewToggle } from './QueueViewToggle'
import { StatTile } from './StatTile'
import { TasksPanel, dueLabel } from './TasksPanel'
import { isOverdue, useContactTasks } from './useContactTasks'
import { LastTouch, latestTouch, useLastTouch } from './useLastTouch'
import { useContactMessages } from './useContactMessages'
import { UseFocusQueueReturn } from './useFocusQueue'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import { useHorizontalSwipe } from 'app/hooks/useHorizontalSwipe'
import { closeModal, showModal } from 'app/lib/modal'
import { cn } from 'app/lib/utils'
import { ContactJson } from 'app/types/entities'
import { getActivityTypeColorMap, getLeadStatusColorMap } from 'lib/colorMappings'

export type FocusModeComponentProps = UseFocusQueueReturn & {
  viewToggle?: ReactNode
  bandPicker?: ReactNode
  /**
   * Drawn inside a list page, like the workflow canvas: it fills the main area beside the
   * sidebar instead of covering the whole window, and there is nothing to exit, since the
   * view switch and the sidebar are the ways out. The Action Center's own route leaves this
   * off and keeps the full-window takeover with its X and Escape.
   */
  embedded?: boolean
}

function formatCallDuration(seconds?: number): string | undefined {
  if (seconds == null || seconds <= 0) return undefined
  const mins = Math.floor(seconds / 60)
  const secs = Math.round(seconds % 60)
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`
}

const QUEUE_ICONS: Record<string, React.ReactNode> = {
  'active-leads': <FireIcon size={18} weight="bold" />,
  'due-for-hygiene': <ToothIcon size={18} weight="bold" />,
  'hygiene-coming-due': <CalendarCheckIcon size={18} weight="bold" />,
  'hygiene-long-past-due': <ClockCounterClockwiseIcon size={18} weight="bold" />,
}

// The drawer's editable lead statuses. Closed Won is system-set, never picked.
const LEAD_STATUS_OPTIONS = [`Needs Action`, `In Progress`, `Scheduled`, `Closed Lost`]

const SNOOZE_PRESETS = [1, 3, 7, 14, 30]

const PLAY_PANEL_BG: Record<ActionPlay[`tone`], string> = {
  danger: `bg-red-500/8`,
  amber: `bg-amber-500/8`,
  blue: `bg-blue-500/8`,
  gray: `bg-surface-muted`,
  green: `bg-green-500/8`,
}

const DATE_TILE_HEAD: Record<ActionPlay[`tone`], string> = {
  danger: `bg-red-600`,
  amber: `bg-amber-500`,
  blue: `bg-blue-600`,
  gray: `bg-fill-neutral`,
  green: `bg-green-600`,
}

const PLAY_LABEL_TEXT: Record<ActionPlay[`tone`], string> = {
  danger: `text-red-900`,
  amber: `text-amber-900`,
  blue: `text-blue-900`,
  gray: `text-gray-strong`,
  green: `text-green-700`,
}

/**
 * The workflow canvas's dot grid (React Flow `Dots`, gap 20, size 1) rebuilt
 * as a CSS background so focus mode does not pull in React Flow for texture.
 */
const DOT_MATRIX: React.CSSProperties = {
  backgroundImage: `radial-gradient(circle, var(--dot-ink) 1px, transparent 1px)`,
  backgroundSize: `var(--dot-gap) var(--dot-gap)`,
}

/**
 * Presentational: every value arrives via props from `useFocusQueue`, which
 * the route-level observer already tracks. Deliberately NOT wrapped in
 * `observer()`: as an observer this component owned its own MobX reaction,
 * which invalidated itself on render and spun the main thread (a 250+
 * renders/sec loop).
 */
export function FocusModeComponent(props: FocusModeComponentProps) {
  const {
    user,
    def,
    contact,
    cardActivity,
    index,
    total,
    loading,
    done,
    sessionWorked,
    sessionValue,
    drawerOpen,
    openProfile,
    closeProfile,
    next,
    prev,
    recordCall,
    recordSms,
    needsActionDateEnabled,
    locationTerm,
    recordTask,
    snooze,
    submitClosedLost,
    submitLeadStatus,
    setPipelineStage,
    removeFromPipeline,
    pipelineSaving,
    onFollowUpLogged,
    applyContactChange,
    exit,
    viewToggle,
    bandPicker,
    embedded = false,
  } = props

  // Only for the view toggle's navigation; the queue reads its location
  // drill-down through `locationTerm`, not this.
  const routerLocation = useLocation()

  // See useLocationsInfo for why this must not be a live subscription.
  const locationsInfo = useLocationsInfo()
  const locationEntry = contact?.locationId ? locationsInfo?.get(contact.locationId) : undefined
  const locationName = locationEntry?.name
  const pipelinesCatalog = usePipelinesCatalog()
  const copy = useListCopy()
  const groupedNav = useFeatureToggle(`grouped-navigation`)

  // Set when "View" jumps from the trigger-activity block into the profile.
  const [highlightActivityId, setHighlightActivityId] = useState<string | undefined>(undefined)

  const [playerOpen, setPlayerOpen] = useState(false)
  useEffect(() => {
    setPlayerOpen(false)
  }, [contact?.id])

  const [snoozeDays, setSnoozeDays] = useState(getPreferredSnoozeDays)

  const [backPanel, setBackPanel] = useState<`thread` | `tasks` | undefined>(undefined)
  const flipped = backPanel != null
  const tasks = useContactTasks(contact)
  const thread = useContactMessages(contact)
  const { touch: activityTouch, loading: touchLoading } = useLastTouch(contact)
  // Texts are not activities, so the thread already in hand supplies that side
  // of the picture and the two are merged by recency.
  const lastTouch = useMemo(() => {
    // Skip optimistic bubbles: they carry a placeholder sender id, not a user.
    const outbound = [...thread.messages].reverse().find((m) => !m.pending && (m.sentByUserId || m.isAgent))
    const textTouch: LastTouch | undefined = outbound?.createdDate
      ? {
          kind: `text`,
          actor: outbound.isAgent ? `AI Agent` : undefined,
          actorUserId: outbound.isAgent ? undefined : (outbound.sentByUserId ?? undefined),
          snippet: outbound.text || undefined,
          at: outbound.createdDate,
        }
      : undefined
    return latestTouch(activityTouch, textTouch)
  }, [activityTouch, thread.messages])
  const touchLoadingNow = touchLoading && !lastTouch
  useEffect(() => {
    setBackPanel(undefined)
  }, [contact?.id])

  const openTasks = () => {
    setBackPanel(`tasks`)
    // The list was last read when the card mounted; a task may have moved since.
    tasks.refresh()
  }

  const openThread = () => {
    setBackPanel(`thread`)
    // Re-read on open: a reply may have landed since the card mounted.
    thread.refresh()
    // Reading the thread is what marks it read, same as opening it anywhere else.
    thread.markAllRead()
  }

  // There is no message subscription, and a live urql query in this component
  // is what caused the render-loop freeze, so a slow poll keeps the thread
  // current. It runs with the card closed too: the unread badge must say a
  // reply landed BEFORE the thread is opened.
  useEffect(() => {
    if (!contact?.id) return
    const timer = setInterval(() => thread.refresh(), flipped ? 10_000 : 30_000)
    return () => clearInterval(timer)
  }, [contact?.id, flipped, thread.refresh])

  const snoozeFor = (days: number) => {
    setSnoozeDays(days)
    setPreferredSnoozeDays(days)
    snooze(days)
  }

  // Arrow keys drive the flow when no overlay is open and focus isn't in an input.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (drawerOpen) return
      // Any open popover/menu/dialog owns the keyboard, especially Escape,
      // which must close the overlay rather than exit focus mode.
      if (document.querySelector(`[data-radix-popper-content-wrapper], [role="dialog"]`)) return
      const target = event.target as HTMLElement | null
      if (target && [`INPUT`, `TEXTAREA`, `SELECT`].includes(target.tagName)) return
      // While the back panel is showing it owns the card: Escape turns the
      // card back, and the queue does not advance under a conversation.
      if (flipped) {
        if (event.key === `Escape`) setBackPanel(undefined)
        return
      }
      if (event.key === `ArrowRight`) next()
      if (event.key === `ArrowLeft`) prev()
      // Embedded, Escape has nowhere to go: the page is the list, not a mode over it.
      if (event.key === `Escape` && !embedded) exit()
    }
    window.addEventListener(`keydown`, handler)
    return () => window.removeEventListener(`keydown`, handler)
  }, [drawerOpen, flipped, next, prev, exit, embedded])

  // Same rules as the arrow keys: nothing moves while the back panel is up.
  const { onTouchStart, onTouchEnd } = useHorizontalSwipe({
    enabled: !flipped,
    onSwipeLeft: next,
    onSwipeRight: () => index > 0 && prev(),
  })

  // Handed to the rail so a contact the board files under "Not started" says
  // so, rather than being offered a pipeline they are already worked in. See
  // `PipelineRail`'s `implicitPipelineId`.
  const boardPipelineId = def?.board?.source === `pipeline` ? def.board.pipelineId : undefined

  if (!def || !user) return null

  const queueName = def.name

  const isLeads = def.id === `active-leads`
  const progress = total > 0 ? Math.min(1, index / total) : 0
  const phone = contact?.cellPhone || contact?.homePhone || contact?.workPhone
  const play = contact ? actionPlayFor(def.id, contact) : undefined
  // Looked up by status from the appointment records, not read off the
  // contact's nearestAppointmentDate pointer.
  const playAppointment = useLatestAppointment(contact, play?.appointmentStatus)
  const appointmentDate = playAppointment?.date
  // A play that carries its own evidence (date tile, recall timeline) does not
  // also need the generic last-call block; a real trigger activity still shows.
  const playHasOwnEvidence = !!appointmentDate || play?.timeline === `hygiene`
  const showCardActivity = !(playHasOwnEvidence && cardActivity?.role === `last-call`)

  const viewCardActivity = () => {
    if (!cardActivity?.activity.id) return
    setHighlightActivityId(cardActivity.activity.id)
    openProfile()
  }

  const validPhones = [contact?.cellPhone, contact?.homePhone, contact?.workPhone]
    .filter(Boolean)
    .filter((p) => (p as string).replace(/\D/g, ``).length === 10)
  const canClickToCall =
    !!locationEntry?.callRailCompanyId && !!locationEntry.primaryTrackingNumber && validPhones.length > 0

  const handleClickToCall = () => {
    if (!contact) return
    recordCall()
    showModal(
      <OutboundCallProvider>
        <CallInitiationModal contact={contact} onContactRefresh={() => applyContactChange(contact)} />
      </OutboundCallProvider>,
      {
        title: `Outbound Call`,
        isFullScreen: false,
        removePadding: true,
        closable: true,
        showMinimizeButton: false,
      },
    )
  }

  // Picking a status stages it and an explicit Save commits, following the
  // app's save loop. Closed Lost gates through its reason modal instead.
  const [pendingStatus, setPendingStatus] = useState<string | undefined>(undefined)

  useEffect(() => {
    setPendingStatus(undefined)
  }, [contact?.id])

  const handleStatusChange = (status: string) => {
    if (status === `Closed Lost`) {
      setPendingStatus(undefined)
      if (contact && user) {
        showClosedLostQuickModal({ contact, user, onSubmit: submitClosedLost, onCancel: () => {} })
      }
      return
    }
    setPendingStatus(status === contact?.leadStatus ? undefined : status)
  }

  const saveStatus = () => {
    if (!pendingStatus) return
    submitLeadStatus(pendingStatus)
    setPendingStatus(undefined)
  }

  const openTaskModal = () => {
    if (!contact) return
    const defaultContact = {
      id: contact.id,
      name: contact.name || `Unknown`,
      locationId: contact.locationId,
    } as ContactJson
    showModal(
      <QuickCreateTaskModal
        defaultContact={defaultContact}
        onTaskCreatedOrUpdated={() => {
          recordTask()
          tasks.refresh()
        }}
      />,
      {
        hideTitle: true,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
      },
    )
  }

  const openFollowUpModal = () => {
    if (!contact) return
    showModal(
      <FollowUpForm
        contact={contact}
        onSubmit={(_activity, followUpResult) => {
          if (followUpResult) onFollowUpLogged()
        }}
        onComplete={closeModal}
      />,
      {
        title: `Add Follow Up`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
      },
    )
  }

  return (
    <div
      className={cn(`flex flex-col bg-surface-subtle`, embedded ? `relative h-full` : `fixed inset-0 z-40`)}
      style={DOT_MATRIX}
    >
      {/* Wraps on a phone: the view switch drops under the list's name instead of being cut
          off at the edge. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-default bg-surface-raised px-4 py-3 md:px-6">
        <div className="flex flex-wrap items-center gap-3 gap-y-2">
          {!embedded && (
            <UiButton variant="subtle" size="icon" onClick={exit} aria-label="Exit focus mode">
              <XIcon size={18} weight="bold" />
            </UiButton>
          )}
          {/* No icon beside the name on a list page, as on its board and table. */}
          {!embedded && (
            <div className={cn(`hidden rounded-lg p-2 md:block`, FAMILY_ACCENT[def.family])}>{QUEUE_ICONS[def.id]}</div>
          )}
          <div className="min-w-0">
            <p className="truncate text-ui-sm text-strong">{queueName}</p>
            <p className="text-body-xs text-subtle">{total > 0 ? `${Math.min(index + 1, total)} of ${total}` : `—`}</p>
          </div>
        </div>
        <div className="hidden w-64 md:block">
          <div className="h-1.5 overflow-hidden rounded-full bg-surface-muted">
            <div
              className="h-full rounded-full bg-primary-600 transition-all"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* A phone has room for one thing on the right: the view switch wins over the session tally. */}
          <div
            className={cn(
              `items-center gap-2 text-body-sm text-subtle`,
              viewToggle || def?.surface === `both` ? `hidden md:flex` : `flex`,
            )}
          >
            <LightningIcon size={16} weight="fill" className="text-amber-500" />
            <span>
              This session: {sessionWorked} worked
              {sessionValue > 0 && <span className="text-green-700"> · ~{formatCurrency(sessionValue)} engaged</span>}
            </span>
          </div>
          {viewToggle ? (
            <div className="flex items-center gap-2">
              {viewToggle}
              {bandPicker}
            </div>
          ) : (
            def?.surface === `both` && (
              <QueueViewToggle queueId={def!.id} userId={user?.id} current="focus" search={routerLocation.search} />
            )
          )}
        </div>
      </div>

      {/* Auto margins, not items-center: centering a child taller than its scroller pushes
          its top out of reach, which cut the contact's name off on a phone. */}
      <div className="flex min-h-0 flex-1 justify-center overflow-y-auto px-4 py-8 *:my-auto">
        {!contact && !done ? (
          // No card means either the first read is still out, or the flow has
          // walked off the end of the pages read so far while the next one is
          // prefetching. Neither is the end of the queue; `done` is the only
          // honest end.
          <div className="w-full max-w-2xl space-y-4">
            <div className="h-40 animate-pulse rounded-2xl bg-surface-muted" />
            <div className="h-16 animate-pulse rounded-2xl bg-surface-muted" />
          </div>
        ) : done || !contact ? (
          // `|| !contact` only ever fires with `done` true, since the branch
          // above took every card-less case that is not the end. It stays
          // because it is what narrows `contact` for the card below.
          <div className="text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-500/8 text-green-700">
              <CheckCircleIcon size={36} weight="fill" />
            </div>
            {/* Stepping past the last card is not emptying the queue: everyone
                skipped is still in it. */}
            <h3 className="gilroy mt-4 text-strong">{total > 0 ? `End of the queue` : `Queue clear`}</h3>
            <p className="mt-2 text-body-md text-subtle">
              {sessionWorked > 0
                ? `You worked ${sessionWorked} ${sessionWorked === 1 ? `contact` : `contacts`} this session${
                    sessionValue > 0 ? `, engaging ~${formatCurrency(sessionValue)} in estimated production` : ``
                  }.`
                : total > 0
                  ? `You reached the end of ${queueName}. ${total} ${total === 1 ? `contact is` : `contacts are`} still waiting.`
                  : `Nothing waiting in ${queueName}.`}
            </p>
            <div className="mt-6 flex items-center justify-center gap-2">
              {total > 0 && index > 0 && (
                <Button mode="white" onClick={prev} className="whitespace-nowrap">
                  <ArrowLeftIcon size={16} weight="bold" className="mr-1.5" /> Back to the queue
                </Button>
              )}
              {!embedded && (
                <Button mode="primary" onClick={exit} className="whitespace-nowrap">
                  Back to Action Center
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center justify-center gap-4 lg:gap-8">
            <NavArrow direction="prev" onClick={prev} disabled={index === 0} />

            <div className="relative w-full max-w-2xl" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <LastActivityPanel
                touch={lastTouch}
                loading={touchLoadingNow}
                onView={
                  lastTouch?.kind === `text`
                    ? openThread
                    : lastTouch?.activityId
                      ? () => {
                          setHighlightActivityId(lastTouch.activityId)
                          openProfile()
                        }
                      : undefined
                }
              />
              {/* Flip stage. The front face stays in normal flow so it sets
                  the height; the back is absolutely positioned to match it,
                  which keeps the turn from resizing mid-animation. */}
              <div className="[perspective:1600px]">
                <div
                  className={cn(
                    `relative transform-3d transition-transform duration-500 ease-out`,
                    flipped && `rotate-y-180`,
                  )}
                >
                  <div className={cn(`backface-hidden`, flipped && `pointer-events-none`)} aria-hidden={flipped}>
                    <div className="rounded-2xl border border-default bg-surface-raised p-8 shadow-elevation-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="gilroy text-strong">{contact.name || `Unknown contact`}</h3>
                          <div className="mt-2 flex flex-wrap items-center gap-3">
                            {phone && (
                              <a
                                href={`tel:${phone}`}
                                onClick={recordCall}
                                className="inline-flex items-center gap-1 text-body-sm tabular-nums text-subtle hover:text-primary-600"
                              >
                                <PhoneIcon size={14} weight="bold" />
                                {formatPhoneNumber(phone)}
                              </a>
                            )}
                            {locationName && (
                              <span className="inline-flex items-center gap-1 text-body-sm text-subtle">
                                <Companies width={13} height={13} className="text-minimal" />
                                {locationName}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center">
                          <Button
                            mode="white"
                            size="sm"
                            className="!rounded-r-none"
                            onClick={() => snoozeFor(snoozeDays)}
                          >
                            <ClockIcon size={14} weight="bold" className="mr-1" /> Snooze {snoozeDays}d
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <span>
                                <Button
                                  mode="white"
                                  size="sm"
                                  className="!rounded-l-none !border-l-0 !px-1.5"
                                  aria-label="Snooze duration"
                                >
                                  <CaretDownIcon size={12} weight="bold" />
                                </Button>
                              </span>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {SNOOZE_PRESETS.map((days) => (
                                <DropdownMenuItem key={days} onClick={() => snoozeFor(days)}>
                                  For {days} {days === 1 ? `day` : `days`}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {play && (
                        <div className={cn(`mt-5 rounded-lg px-4 py-3`, PLAY_PANEL_BG[play.tone])}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className={cn(`text-ui-sm`, PLAY_LABEL_TEXT[play.tone])}>{play.label}</p>
                              {(() => {
                                const facts = [play.detail?.(contact), def.whyLine?.(contact)]
                                  .filter(Boolean)
                                  .join(` · `)
                                return facts ? <p className="mt-0.5 text-body-sm text-default">{facts}</p> : null
                              })()}
                            </div>
                            {appointmentDate && (
                              <DateTile date={appointmentDate} headClass={DATE_TILE_HEAD[play.tone]} utc />
                            )}
                          </div>
                          {play.timeline === `hygiene` && <HygieneTimeline contact={contact} />}
                          {showCardActivity && cardActivity && (
                            <div className="mt-2.5 rounded-md border border-subtle bg-surface/80 px-3 py-2.5">
                              <div className="flex items-center justify-between gap-2">
                                <p className="text-body-xs text-subtle">
                                  <span
                                    className="mr-1.5 inline-block h-1.5 w-1.5 align-middle"
                                    style={{
                                      backgroundColor: getActivityTypeColorMap(cardActivity.activity.type),
                                    }}
                                  />
                                  {cardActivity.role === `last-call` ? `Last call` : cardActivity.activity.type}
                                  {cardActivity.activity.subType ? ` · ${cardActivity.activity.subType}` : ``}
                                  {cardActivity.activity.activityDate
                                    ? ` · ${moment(cardActivity.activity.activityDate).format(`MMM D, h:mm A`)}`
                                    : ``}
                                  {formatCallDuration(cardActivity.call?.duration)
                                    ? ` · ${formatCallDuration(cardActivity.call?.duration)}`
                                    : ``}
                                  {cardActivity.call?.answered === false ? ` · not answered` : ``}
                                </p>
                                <span className="shrink-0 text-body-xs">
                                  <UiButton variant="link" size="inline" onClick={viewCardActivity}>
                                    View
                                  </UiButton>
                                </span>
                              </div>
                              {(() => {
                                // A call's real content is the AI summary off the Call
                                // record; the activity row's description is boilerplate.
                                const snippet =
                                  cardActivity.call?.summary ||
                                  cardActivity.activity.description ||
                                  cardActivity.activity.note
                                return snippet ? (
                                  <p className="mt-1 line-clamp-3 text-body-sm text-default">{snippet}</p>
                                ) : null
                              })()}
                              {cardActivity.call?.recordingUrl &&
                                (playerOpen ? (
                                  <div className="mt-2">
                                    <AudioPlayer
                                      key={cardActivity.activity.id}
                                      id={cardActivity.activity.id!}
                                      callId={cardActivity.call.callId}
                                      className="p-0"
                                      imageHeight="h-7"
                                      url={cardActivity.call.recordingUrl}
                                      waveforms={cardActivity.call.waveforms}
                                      urlNotFoundMessage="There was an error loading this call's audio."
                                    />
                                  </div>
                                ) : (
                                  // Mounted on demand: the player is heavy (waveform
                                  // image + ResizeObserver) and most cards get skimmed.
                                  <UiButton
                                    variant="white"
                                    size="xs"
                                    onClick={() => setPlayerOpen(true)}
                                    className="mt-2"
                                  >
                                    <PlayIcon size={12} weight="fill" /> Play recording
                                  </UiButton>
                                ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <StatTile label="Tasks">
                          {tasks.loading && tasks.openCount === 0 ? (
                            <span className="text-minimal">…</span>
                          ) : tasks.next ? (
                            <button
                              type="button"
                              onClick={openTasks}
                              className="flex min-w-0 max-w-full items-baseline gap-1.5 text-left hover:underline"
                            >
                              <span className="min-w-0 truncate">{tasks.next.title || `Untitled task`}</span>
                              <span
                                className={cn(
                                  `shrink-0 text-body-xs`,
                                  isOverdue(tasks.next) ? `text-red-600` : `text-subtle`,
                                )}
                              >
                                {dueLabel(tasks.next)}
                              </span>
                              {tasks.openCount > 1 && (
                                <span className="shrink-0 text-body-xs text-minimal">+{tasks.openCount - 1}</span>
                              )}
                            </button>
                          ) : (
                            <span className="text-body-xs">
                              <UiButton variant="subtle" size="inline" onClick={openTasks}>
                                None open
                              </UiButton>
                            </span>
                          )}
                        </StatTile>
                        <StatTile label="Last contact">
                          {contact.lastCommDate ? moment(contact.lastCommDate).fromNow() : `No contact yet`}
                        </StatTile>
                        <StatTile label="Next appointment">
                          {
                            // nearestAppointmentDate can hold a past appointment; only
                            // a future date is honestly a "next" appointment.
                            contact.nearestAppointmentDate &&
                            moment(contact.nearestAppointmentDate).isSameOrAfter(moment(), `day`)
                              ? moment(contact.nearestAppointmentDate).format(`MMM D, YYYY`)
                              : `None scheduled`
                          }
                        </StatTile>
                        {!isLeads && (
                          <StatTile label="Lifetime production">
                            {typeof contact.productionLtv === `number` && contact.productionLtv > 0
                              ? formatCurrency(contact.productionLtv)
                              : `None recorded`}
                          </StatTile>
                        )}
                        <StatTile label="Lead status">
                          {!isLeads ? null : (
                            <div className="flex min-w-0 items-center gap-1.5">
                              {/* A picked status stages (amber ring) until Save commits. */}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <LeadStatusTrigger
                                    status={(pendingStatus ?? contact.leadStatus) as ContactJson[`leadStatus`]}
                                    className={cn(pendingStatus && `border-amber-400 hover:border-amber-500`)}
                                  />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                  {LEAD_STATUS_OPTIONS.map((status) => (
                                    <DropdownMenuItem key={status} onClick={() => handleStatusChange(status)}>
                                      <span
                                        className="mr-2 inline-block h-1.5 w-1.5"
                                        style={{
                                          backgroundColor: getLeadStatusColorMap(status as ContactJson[`leadStatus`]),
                                        }}
                                      />
                                      {status}
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                              {pendingStatus && (
                                <>
                                  <Button mode="primary" size="xs" onClick={saveStatus}>
                                    Save
                                  </Button>
                                  <span className="shrink-0 text-body-xs">
                                    <UiButton
                                      variant="subtle"
                                      size="inline"
                                      onClick={() => setPendingStatus(undefined)}
                                    >
                                      Cancel
                                    </UiButton>
                                  </span>
                                </>
                              )}
                            </div>
                          )}
                        </StatTile>
                        <StatTile label={copy.Noun} className="sm:col-span-2" wrap>
                          {groupedNav ? (
                            <PracticeListRail
                              contact={contact}
                              implicitPipelineId={boardPipelineId}
                              savingId={pipelineSaving}
                              onMoveStage={setPipelineStage}
                              onRemove={removeFromPipeline}
                              onJoin={setPipelineStage}
                            />
                          ) : pipelinesCatalog?.length ? (
                            <PipelineRail
                              contact={contact}
                              catalog={pipelinesCatalog}
                              implicitPipelineId={boardPipelineId}
                              savingId={pipelineSaving}
                              onMoveStage={setPipelineStage}
                              onRemove={removeFromPipeline}
                              onJoin={setPipelineStage}
                            />
                          ) : null}
                        </StatTile>
                      </div>
                    </div>
                  </div>

                  {/* Reverse face. Sized to the front by `inset-0`, pre-rotated
                      so it lands upright when the stage turns. */}
                  <div
                    className={cn(`absolute inset-0 rotate-y-180 backface-hidden`, !flipped && `pointer-events-none`)}
                    aria-hidden={!flipped}
                  >
                    {/* Keyed per contact: a text drafted to one patient must never be waiting for the next. */}
                    {backPanel === `tasks` ? (
                      <TasksPanel
                        key={contact.id}
                        contact={contact}
                        open={tasks.open}
                        done={tasks.done}
                        loading={tasks.loading}
                        saving={tasks.saving}
                        active={backPanel === `tasks`}
                        onToggle={tasks.setStatus}
                        onReschedule={tasks.reschedule}
                        onNewTask={openTaskModal}
                        onBack={() => setBackPanel(undefined)}
                      />
                    ) : (
                      <ConversationPanel
                        key={contact.id}
                        contact={contact}
                        messages={thread.messages}
                        loading={thread.loading}
                        sending={thread.sending}
                        failed={thread.failed}
                        active={flipped}
                        onSend={async (text) => {
                          const ok = await thread.send(text)
                          if (ok) recordSms()
                          return ok
                        }}
                        onBack={() => setBackPanel(undefined)}
                        onRetry={thread.refresh}
                      />
                    )}
                  </div>
                </div>
              </div>

              <FocusActionBar
                play={play}
                backPanel={backPanel}
                contact={contact}
                phone={phone}
                canClickToCall={canClickToCall}
                handleClickToCall={handleClickToCall}
                recordCall={recordCall}
                thread={thread}
                tasks={tasks}
                openThread={openThread}
                openTasks={openTasks}
                onTurnBack={() => setBackPanel(undefined)}
                openFollowUpModal={openFollowUpModal}
                openProfile={openProfile}
              />

              <div className="mt-3 flex items-center justify-between text-body-sm md:hidden">
                <UiButton variant="subtle" size="inline" onClick={prev} disabled={index === 0}>
                  <ArrowLeftIcon size={14} weight="bold" /> Previous
                </UiButton>
                <UiButton variant="subtle" size="inline" onClick={next}>
                  Next <ArrowRightIcon size={14} weight="bold" />
                </UiButton>
              </div>
            </div>

            <NavArrow direction="next" onClick={next} />
          </div>
        )}
      </div>

      <ContactDrawer
        openDrawer={drawerOpen}
        contact={drawerOpen ? contact : undefined}
        highlightActivityId={highlightActivityId}
        onClose={() => {
          setHighlightActivityId(undefined)
          closeProfile()
        }}
        onChange={applyContactChange}
        nextContact={next}
        previousContact={prev}
      />
    </div>
  )
}
